import { StringKey } from '../../../../lang/index';

export enum GeneralCapTableTab {
  CAP_TABLE = 'cap-table',
  LIQUIDITY = 'liquidity',
}

export enum CapTableTab {
  BY_SHARE_CLASS = 'by-share-class',
  BY_STAKEHOLDER = 'by-stakeholder',
}

export const CapTableTabTitle: Record<CapTableTab, StringKey> = {
  [CapTableTab.BY_STAKEHOLDER]: 'BY_STAKEHOLDER',
  [CapTableTab.BY_SHARE_CLASS]: 'BY_SHARE_CLASS',
};

export enum StakeholdersViewBy {
  STAKEHOLDERS = 'stakeholders',
  TYPE = 'type',
}

export enum Column {
  AVERAGE_SHARE_PRICE = 'average-share-price',
  CAP_PARTICIPATING = 'cap-participating',
  COMMON_STOCK = 'common-stock',
  COMMON_STOCK_PROCEEDS = 'common-stock-proceeds',
  DILUTED = 'diluted',
  DILUTED_SHARES = 'diluted-shares',
  EFFECTIVE_LIQUIDITY_PROCEEDS = 'effective-liquidity-proceeds',
  EMPTY = 'empty',
  INVESTMENT = 'investment',
  INVESTMENT_TIME = 'investment-time',
  ISSUED = 'issued',
  MIN_LIQUIDITY_PROCEEDS = 'min-liquidity-proceeds',
  MULTIPLE = 'multiple',
  NAME = 'name',

  NO_ISSUED = 'no-issued',
  OTHER_SHARE_CLASSES = 'other-share-classes',
  OTHER_SHARE_CLASSES_PROCEEDS = 'other-share-classes-proceeds',
  PARTICIPATING_ON_PROCEEDS = 'participating-on-proceeds',
  PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS = 'proceeds-without-liquidity-proceeds',
  ROI = 'roi',
  SENIORITY = 'seniority',
  STAKEHOLDER_NAME = 'stakeholder-name',
  TYPE = 'type',
  VOTING = 'voting',
  VOTING_RIGHTS = 'voting-rights',
}

export const columnTitle: Record<Column, StringKey> = {
  [Column.NAME]: 'NAME',
  [Column.STAKEHOLDER_NAME]: 'STAKEHOLDER_NAME',
  [Column.TYPE]: 'TYPE',
  [Column.ISSUED]: 'ISSUED_PERCENTAGE',
  [Column.DILUTED]: 'DILUTED_PERCENTAGE',
  [Column.VOTING]: 'VOTING_PERCENTAGE',
  [Column.NO_ISSUED]: 'NO_ISSUED',
  [Column.DILUTED_SHARES]: 'DILUTED_SHARES',
  [Column.COMMON_STOCK]: 'COMMON_STOCK',
  [Column.OTHER_SHARE_CLASSES]: 'OTHER_SHARE_CLASSES',
  [Column.OTHER_SHARE_CLASSES_PROCEEDS]: 'OTHER_SHARE_CLASSES',
  [Column.INVESTMENT]: 'INVESTMENT',
  [Column.AVERAGE_SHARE_PRICE]: 'AVERAGE_SHARE_PRICE',
  [Column.VOTING_RIGHTS]: 'VOTING_RIGHTS',
  [Column.EMPTY]: 'EMPTY',
  [Column.INVESTMENT_TIME]: 'INVESTMENT_TIME',
  [Column.CAP_PARTICIPATING]: 'CAP_PARTICIPATING',
  [Column.MULTIPLE]: 'MULTIPLE',
  [Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS]: 'PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS',
  [Column.MIN_LIQUIDITY_PROCEEDS]: 'MIN_LIQUIDITY_PROCEEDS',
  [Column.SENIORITY]: 'SENIORITY',
  [Column.PARTICIPATING_ON_PROCEEDS]: 'PARTICIPATING_ON_PROCEEDS',
  [Column.COMMON_STOCK_PROCEEDS]: 'COMMON_STOCK_PROCEEDS',
  [Column.EFFECTIVE_LIQUIDITY_PROCEEDS]: 'EFFECTIVE_LIQUIDITY_PROCEEDS',
  [Column.ROI]: 'ROI',
};

export type StakeholderColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.COMMON_STOCK
  | Column.COMMON_STOCK_PROCEEDS
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.EFFECTIVE_LIQUIDITY_PROCEEDS
  | Column.INVESTMENT
  | Column.ISSUED
  | Column.MIN_LIQUIDITY_PROCEEDS
  | Column.NAME
  | Column.NO_ISSUED
  | Column.OTHER_SHARE_CLASSES
  | Column.OTHER_SHARE_CLASSES_PROCEEDS
  | Column.PARTICIPATING_ON_PROCEEDS
  | Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS
  | Column.ROI
  | Column.SENIORITY
  | Column.STAKEHOLDER_NAME
  | Column.TYPE
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export type ShareClassColumns =
  | Column.AVERAGE_SHARE_PRICE
  | Column.CAP_PARTICIPATING
  | Column.COMMON_STOCK_PROCEEDS
  | Column.DILUTED
  | Column.DILUTED_SHARES
  | Column.EFFECTIVE_LIQUIDITY_PROCEEDS
  | Column.INVESTMENT
  | Column.INVESTMENT_TIME
  | Column.ISSUED
  | Column.MIN_LIQUIDITY_PROCEEDS
  | Column.MULTIPLE
  | Column.NAME
  | Column.NO_ISSUED
  | Column.PARTICIPATING_ON_PROCEEDS
  | Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS
  | Column.ROI
  | Column.SENIORITY
  | Column.VOTING
  | Column.VOTING_RIGHTS;

export type ColumnVariant = ShareClassColumns | StakeholderColumns;

export const customizeColumns: Record<CapTableTab, ColumnVariant[]> = {
  [CapTableTab.BY_STAKEHOLDER]: [
    Column.NAME,
    Column.STAKEHOLDER_NAME,
    Column.TYPE,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.INVESTMENT_TIME,
    Column.CAP_PARTICIPATING,
    Column.MULTIPLE,
    Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
    Column.MIN_LIQUIDITY_PROCEEDS,
    Column.SENIORITY,
    Column.PARTICIPATING_ON_PROCEEDS,
    Column.COMMON_STOCK_PROCEEDS,
    Column.EFFECTIVE_LIQUIDITY_PROCEEDS,
    Column.ROI,
  ],
  [CapTableTab.BY_SHARE_CLASS]: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.INVESTMENT,
    Column.AVERAGE_SHARE_PRICE,
    Column.MULTIPLE,
    Column.CAP_PARTICIPATING,
    Column.INVESTMENT_TIME,
    Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
    Column.MIN_LIQUIDITY_PROCEEDS,
    Column.SENIORITY,
    Column.PARTICIPATING_ON_PROCEEDS,
    Column.COMMON_STOCK_PROCEEDS,
    Column.EFFECTIVE_LIQUIDITY_PROCEEDS,
    Column.ROI,
  ],
};

const columnToOmitInDefaultView: Column[] = [
  Column.MULTIPLE,
  Column.CAP_PARTICIPATING,
  Column.INVESTMENT_TIME,
  Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
  Column.MIN_LIQUIDITY_PROCEEDS,
  Column.SENIORITY,
  Column.PARTICIPATING_ON_PROCEEDS,
  Column.COMMON_STOCK_PROCEEDS,
  Column.OTHER_SHARE_CLASSES_PROCEEDS,
];

export type CustomView = Record<
  CapTableTab,
  {
    columns: Column[];
    viewBy?: Record<StakeholdersViewBy, boolean>;
  }
>;
const columnsMap = Object.values(Column);

export const expandedColumnMap = columnsMap;

export const defaultColumnMap = columnsMap.filter(
  (column) => !columnToOmitInDefaultView.includes(column),
);

export const capTableTabTitleTitleMap = Object.entries(CapTableTabTitle);

export const defaultCustomView = {
  [CapTableTab.BY_SHARE_CLASS]: {
    columns: expandedColumnMap,
  },
  [CapTableTab.BY_STAKEHOLDER]: {
    columns: expandedColumnMap,
    viewBy: {
      [StakeholdersViewBy.STAKEHOLDERS]: true,
      [StakeholdersViewBy.TYPE]: true,
    },
  },
};

export const stakeholdersViewByColumns: Record<'all' | StakeholdersViewBy, ColumnVariant[]> = {
  all: [
    Column.NAME,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.OTHER_SHARE_CLASSES_PROCEEDS,
    Column.AVERAGE_SHARE_PRICE,
    Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
    Column.MIN_LIQUIDITY_PROCEEDS,
    Column.SENIORITY,
    Column.PARTICIPATING_ON_PROCEEDS,
    Column.COMMON_STOCK_PROCEEDS,
    Column.EFFECTIVE_LIQUIDITY_PROCEEDS,
    Column.ROI,
  ],
  [StakeholdersViewBy.STAKEHOLDERS]: [
    Column.STAKEHOLDER_NAME,
    Column.TYPE,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.OTHER_SHARE_CLASSES_PROCEEDS,
    Column.AVERAGE_SHARE_PRICE,
    Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
    Column.MIN_LIQUIDITY_PROCEEDS,
    Column.SENIORITY,
    Column.PARTICIPATING_ON_PROCEEDS,
    Column.COMMON_STOCK_PROCEEDS,
    Column.EFFECTIVE_LIQUIDITY_PROCEEDS,
    Column.ROI,
  ],
  [StakeholdersViewBy.TYPE]: [
    Column.TYPE,
    Column.ISSUED,
    Column.DILUTED,
    Column.VOTING,
    Column.NO_ISSUED,
    Column.DILUTED_SHARES,
    Column.VOTING_RIGHTS,
    Column.COMMON_STOCK,
    Column.OTHER_SHARE_CLASSES,
    Column.INVESTMENT,
    Column.OTHER_SHARE_CLASSES_PROCEEDS,
    Column.AVERAGE_SHARE_PRICE,
    Column.PROCEEDS_WITHOUT_LIQUIDITY_PROCEEDS,
    Column.MIN_LIQUIDITY_PROCEEDS,
    Column.SENIORITY,
    Column.PARTICIPATING_ON_PROCEEDS,
    Column.COMMON_STOCK_PROCEEDS,
    Column.EFFECTIVE_LIQUIDITY_PROCEEDS,
    Column.ROI,
  ],
};

export const shareClassesViewByColumns: ColumnVariant[] =
  customizeColumns[CapTableTab.BY_SHARE_CLASS];

export type CapTableRowValue = { change: number; value: number };

export type ScenarioFundraisingCapTableRow = {
  avgSharePrice: CapTableRowValue;
  convertedBasis: CapTableRowValue;
  convertedBasisPercentage: CapTableRowValue;
  defaultShareClass: { id: string; name: string } & CapTableRowValue;
  diluted: CapTableRowValue;
  dilutedPercentage: CapTableRowValue;
  investment: CapTableRowValue;
  issued: CapTableRowValue;
  issuedPercentage: CapTableRowValue;
  shareClasses: ({ id: string; name: string } & CapTableRowValue)[];
  unvested?: CapTableRowValue;
  unvestedPercentage?: CapTableRowValue;
  votingPercentage: CapTableRowValue;
  votingRights: CapTableRowValue;
};
